import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { 
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogActions, 
    Button, 
    CircularProgress,
    Box,
    Typography,
    Alert,
    FormControlLabel,
    Checkbox,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Backdrop,
    Stepper,
    Step,
    StepLabel,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    IconButton,
    Tooltip
} from '@mui/material';
import LockClockIcon from '@mui/icons-material/LockClock';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import BlockIcon from '@mui/icons-material/Block';
import { FinanceService } from '../../../api/services/financeService';
import { ReservationService } from '../../../api/services/reservationService';
import { RoomService } from '../../../api/services/roomService';
import { exportClientSide, convertCsvToExportFormat } from '../../../utils/exportUtils';
import ExportFormatModal from '../../../components/ExportFormatModal';
import CancelReservationModal from '../../../components/CancelReservationModal';
import OrderDetailsDrawer from '../../Restaurant/components/OrderDetailsDrawer';
import { toast } from '../../../utils/toast';
import { formatCurrency, useCurrency, getActiveCurrency } from '../../../utils/currency';
import { isUaeProperty } from '../../../utils/dubaiHospitalityTax';
import { standardSecondaryButtonSx } from '../../../components/common/tableStyles';

const STEPS = ['Pending Arrivals', 'Expected Departures', 'Unsettled Folios', 'Unposted Charges', 'Room Discrepancies', 'City Ledger', 'Cashier Closures', 'Exception Report', 'Execute Night Audit'];

export default function NightAuditModal({ open, onClose, onSuccess }) {
    const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
    const tenantId = authData.tenantId || '1';
    const { tenants } = useSelector((state) => state.auth || { tenants: [] });
    const tenant = tenants.find(t => String(t.tenantId) === String(tenantId) || String(t.id) === String(tenantId) || String(t._id) === String(tenantId));
    const hotelStatus = tenant?.status || 'Active';
    const isBypassMode = hotelStatus === 'Inactive' || hotelStatus === 'Suspended';

    const { currency: contextCurrency } = useCurrency();
    const effectiveCurrency = React.useMemo(() => {
        if (tenant?.currency) return String(tenant.currency).toUpperCase();
        if (isUaeProperty(tenant) || isUaeProperty(authData?.hotel) || isUaeProperty(authData?.tenant)) return 'AED';
        const country = (tenant?.country || authData?.hotel?.country || authData?.tenant?.country || '').toLowerCase();
        if (country.includes('india')) return 'INR';
        if (contextCurrency && contextCurrency !== 'USD') return contextCurrency;
        return getActiveCurrency() || 'INR';
    }, [tenant, authData, contextCurrency]);

    const formatPrice = (amt) => formatCurrency(parseFloat(amt || 0), effectiveCurrency);

    const [activeStep, setActiveStep] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isConfirmed, setIsConfirmed] = useState(false);
    const [isDownloadingException, setIsDownloadingException] = useState(false);
    const [exportModalOpen, setExportModalOpen] = useState(false);

    // Audit Data States
    const [pendingArrivals, setPendingArrivals] = useState([]);
    const [cancelModal, setCancelModal] = useState({ open: false, reservation: null });
    const [expectedDepartures, setExpectedDepartures] = useState([]);
    const [unsettledFolios, setUnsettledFolios] = useState([]);
    const [unpostedCharges, setUnpostedCharges] = useState([]);
    const [unpostedSummary, setUnpostedSummary] = useState(null);
    const [roomsMap, setRoomsMap] = useState({});
    const [selectedOrderId, setSelectedOrderId] = useState(null);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [orderDetailsOpen, setOrderDetailsOpen] = useState(false);
    const [roomDiscrepancies, setRoomDiscrepancies] = useState([]);
    const [cityLedger, setCityLedger] = useState([]);
    const [reconcilePayments, setReconcilePayments] = useState([]);
    const [voidedTransactions, setVoidedTransactions] = useState([]);
    const [cashierShiftsClosed, setCashierShiftsClosed] = useState(false);
    const [loadingData, setLoadingData] = useState(false);

    const fetchStepData = async () => {
        setLoadingData(true);
        try {
            if (activeStep === 0) {
                const arrData = await FinanceService.getExpectedArrivals();
                setPendingArrivals(Array.isArray(arrData) ? arrData : (arrData?.arrivals || arrData?.reservations || []));
            } else if (activeStep === 1) {
                const depData = await FinanceService.getExpectedDepartures();
                setExpectedDepartures(Array.isArray(depData) ? depData : (depData?.departures || depData?.reservations || []));
            } else if (activeStep === 2) {
                const ufData = await FinanceService.getUnsettledFolios();
                setUnsettledFolios(Array.isArray(ufData) ? ufData : (ufData?.unsettledFolios || ufData?.folios || ufData?.unsettled || ufData?.data || []));
            } else if (activeStep === 3) {
                const unpostedRes = await FinanceService.getUnpostedCharges();
                const orders = Array.isArray(unpostedRes) ? unpostedRes : (unpostedRes?.orders || []);
                setUnpostedCharges(orders);
                setUnpostedSummary(Array.isArray(unpostedRes) ? null : unpostedRes);
            } else if (activeStep === 4) {
                const discData = await FinanceService.getRoomStatusDiscrepancies();
                setRoomDiscrepancies(Array.isArray(discData) ? discData : (discData?.discrepancies || discData?.rooms || []));
            } else if (activeStep === 5) {
                const clData = await FinanceService.getCityLedger();
                setCityLedger(Array.isArray(clData) ? clData : (clData?.ledgers || clData?.receivables || clData?.data || []));
            } else if (activeStep === 6) {
                const payData = await FinanceService.getReconcilePayments();
                const payments = Array.isArray(payData) ? payData : (payData?.payments || payData?.methods || payData?.data || []);
                setReconcilePayments(payments);
                
                const voidTrackerData = await FinanceService.getVoidTracker();
                setVoidedTransactions(Array.isArray(voidTrackerData) ? voidTrackerData : (voidTrackerData?.tracker || voidTrackerData?.transactions || voidTrackerData?.voids || []));

                const hasPending = payments.some(p => {
                    const variance = parseFloat(p.variance || 0);
                    const status = p.status || 'Closed';
                    return variance > 0 || (status !== 'Closed' && status !== 'Reconciled');
                });
                setCashierShiftsClosed(!hasPending);
            } else if (activeStep === 7) {
                // Ensure unposted charges are verified on Exception Report step if jumped
                if (unpostedCharges.length === 0) {
                    try {
                        const unpostedRes = await FinanceService.getUnpostedCharges();
                        const orders = Array.isArray(unpostedRes) ? unpostedRes : (unpostedRes?.orders || []);
                        setUnpostedCharges(orders);
                        setUnpostedSummary(Array.isArray(unpostedRes) ? null : unpostedRes);
                    } catch (_) {}
                }
            }
        } catch (error) {
            console.error(`Failed to fetch audit data for step ${activeStep}:`, error);
            toast.error('Could not load audit data for this step.');
        } finally {
            setLoadingData(false);
        }
    };

    useEffect(() => {
        if (!open) {
            setActiveStep(0);
            setIsConfirmed(false);
            setCashierShiftsClosed(false);
            setPendingArrivals([]);
            setExpectedDepartures([]);
            setUnsettledFolios([]);
            setUnpostedCharges([]);
            setUnpostedSummary(null);
            setRoomDiscrepancies([]);
            setCityLedger([]);
            setReconcilePayments([]);
            setVoidedTransactions([]);
            setSelectedOrderId(null);
            setSelectedOrder(null);
            setOrderDetailsOpen(false);
            return;
        }

        RoomService.getAll({ noPagination: true }).then((res) => {
            const list = res?.data || (Array.isArray(res) ? res : []);
            const map = {};
            list.forEach(r => {
                if (r.id) map[r.id] = r.roomNumber || r.number || r.name;
                if (r._id) map[r._id] = r.roomNumber || r.number || r.name;
            });
            setRoomsMap(map);
        }).catch((err) => {
            console.warn('Failed to load room map for night audit:', err);
        });

        fetchStepData();
    }, [open, activeStep]);

    const handleNext = () => setActiveStep((prev) => prev + 1);
    const handleBack = () => setActiveStep((prev) => prev - 1);

    const handleDownloadExceptionReport = async (format) => {
        setExportModalOpen(false);
        setIsDownloadingException(true);
        try {
            const date = new Date().toISOString().split('T')[0];
            const res = await FinanceService.generateExceptionReport({ format: 'csv', date });
            
            let csvText = '';
            if (res.data instanceof Blob) {
                csvText = await res.data.text();
            } else if (typeof res.data === 'string') {
                csvText = res.data;
            } else {
                csvText = JSON.stringify(res.data || res);
            }

            const fileNameBase = `night_audit_exceptions_report_${date}`;
            await convertCsvToExportFormat(csvText, format, fileNameBase);

            toast.success('Exception Report downloaded successfully');
        } catch (error) {
            console.error('Download exception report error', error);
            toast.error('Failed to generate exception report');
        } finally {
            setIsDownloadingException(false);
        }
    };

    const handleMarkNoShow = async (id, rowTenantId) => {
        try {
            await ReservationService.markNoShow(id, rowTenantId || tenantId);
            toast.success('Marked as No-Show');
            fetchStepData();
        } catch (error) {
            console.error(error);
            toast.error(error?.response?.data?.message || 'Failed to mark as No-Show');
        }
    };

    const handleOpenCancelModal = (reservation) => {
        setCancelModal({ open: true, reservation });
    };

    const handleSubmit = async () => {
        if (!isConfirmed) {
            toast.error('You must explicitly confirm to execute the Night Audit.');
            return;
        }

        if (pendingArrivals.length > 0) {
            toast.error('Cannot execute night audit with pending arrivals. Please resolve them first.');
            return;
        }

        if (expectedDepartures.length > 0) {
            toast.error('Cannot execute night audit with pending departures. Ensure guests check out or extend their stay.');
            return;
        }

        if (unpostedCharges.length > 0) {
            toast.error('Cannot execute night audit with unposted charges. Please post them to folios first.');
            return;
        }

        if (roomDiscrepancies.length > 0) {
            toast.error('Cannot execute night audit with unresolved room status discrepancies.');
            return;
        }

        if (!cashierShiftsClosed) {
            toast.error('Cannot execute night audit with open cashier shifts. Please reconcile and close them first.');
            return;
        }

        setIsSubmitting(true);
        try {
            await FinanceService.runNightAudit();
            
            toast.success('Daily night audit completed successfully; system business date rolled over');
            onSuccess();
            onClose();
        } catch (error) {
            console.error('Failed to run Night Audit:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString();
    };

    const renderPendingArrivals = () => (
        <Box>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                Review Pending Arrivals
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Please process all expected arrivals before running the Night Audit. Mark them as No-Show or Cancel if they have not arrived.
            </Typography>
            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Res ID</TableCell>
                            <TableCell>Guest Name</TableCell>
                            <TableCell>Check-In</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loadingData ? (
                            <TableRow><TableCell colSpan={4} align="center"><CircularProgress size={24} /></TableCell></TableRow>
                        ) : pendingArrivals.length === 0 ? (
                            <TableRow><TableCell colSpan={4} align="center">No pending arrivals.</TableCell></TableRow>
                        ) : (
                            pendingArrivals.map((item) => {
                                const res = item.reservation || item;
                                const guestDisplayName = res.guest?.name || item.guest?.name || res.guestName || (res.guest?.firstName ? `${res.guest.firstName} ${res.guest.lastName || ''}`.trim() : null) || 'N/A';
                                return (
                                <TableRow key={res.id || Math.random()}>
                                    <TableCell>{res.bookingId || (res.id ? res.id.split('-')[0] : 'N/A')}</TableCell>
                                    <TableCell>{guestDisplayName}</TableCell>
                                    <TableCell>{formatDate(res.checkIn)}</TableCell>
                                    <TableCell align="right">
                                        <Tooltip title="Mark No-Show">
                                            <Button size="small" variant="outlined" color="warning" sx={{ mr: 1 }} onClick={() => handleMarkNoShow(res.id, res.tenantId)}>
                                                No-Show
                                            </Button>
                                        </Tooltip>
                                        <Tooltip title="Cancel Booking">
                                            <IconButton size="small" color="error" onClick={() => handleOpenCancelModal(res)}>
                                                <BlockIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );

    const renderExpectedDepartures = () => (
        <Box>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                Expected Departures & Outstanding Balances
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                The following in-house guests are due to depart today or earlier. They must be checked out or their reservation extended before the audit runs.
            </Typography>
            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Res/Group ID</TableCell>
                            <TableCell>Guest Name</TableCell>
                            <TableCell>Payment Status</TableCell>
                            <TableCell align="right">Total Amount</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loadingData ? (
                            <TableRow><TableCell colSpan={4} align="center"><CircularProgress size={24} /></TableCell></TableRow>
                        ) : expectedDepartures.length === 0 ? (
                            <TableRow><TableCell colSpan={4} align="center">All departures have been processed.</TableCell></TableRow>
                        ) : (
                            expectedDepartures.map((item) => {
                                const res = item.reservation || item;
                                const owesMoney = res.paymentStatus === 'Due' || res.paymentStatus === 'Unpaid' || res.paymentStatus === 'Partial';
                                return (
                                    <TableRow key={res.id || Math.random()}>
                                        <TableCell>{res.bookingId || (res.id ? res.id.split('-')[0] : 'N/A')}</TableCell>
                                        <TableCell>{res.guest?.name || item.guest?.name || 'N/A'}</TableCell>
                                        <TableCell>
                                            <Chip label={res.paymentStatus || 'N/A'} color={owesMoney ? "warning" : "success"} size="small" />
                                        </TableCell>
                                        <TableCell align="right">{formatPrice(res.totalAmount || item.outstandingBalance || 0)}</TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );

    const renderUnsettledFolios = () => (
        <Box>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                Unsettled Folios
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                The following in-house guests or groups have outstanding balances. Ensure all charges are posted correctly.
            </Typography>
            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Res/Group ID</TableCell>
                            <TableCell>Guest Name</TableCell>
                            <TableCell>Payment Status</TableCell>
                            <TableCell align="right">Total Amount</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loadingData ? (
                            <TableRow><TableCell colSpan={4} align="center"><CircularProgress size={24} /></TableCell></TableRow>
                        ) : unsettledFolios.length === 0 ? (
                            <TableRow><TableCell colSpan={4} align="center">All folios are settled.</TableCell></TableRow>
                        ) : (
                            unsettledFolios.map((res) => (
                                <TableRow key={res.id || res.folioId || res.bookingId}>
                                    <TableCell>{res.bookingId || res.folioId || (res.id && res.id.split('-')[0])}</TableCell>
                                    <TableCell>{res.guest?.name || res.guestName || 'N/A'}</TableCell>
                                    <TableCell>
                                        <Chip label={res.paymentStatus || 'Due'} color="warning" size="small" />
                                    </TableCell>
                                    <TableCell align="right">{formatPrice(res.totalAmount || res.amount || 0)}</TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );

    const renderUnpostedCharges = () => {
        const totalCalculated = unpostedCharges.reduce((sum, item) => sum + parseFloat(item.totalAmount || item.amount || 0), 0);
        const totalAmount = unpostedSummary?.unpostedAmount !== undefined ? parseFloat(unpostedSummary.unpostedAmount) : totalCalculated;
        const count = unpostedSummary?.unpostedCount !== undefined ? unpostedSummary.unpostedCount : unpostedCharges.length;

        return (
            <Box>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                    Detect Unposted Charges
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Ensure all completed Restaurant/External orders are billed to a Folio before audit.
                </Typography>

                {/* Summary Metrics */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2, mb: 3 }}>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: '#f8fafc' }}>
                        <Typography variant="caption" color="text.secondary" display="block">Business Date</Typography>
                        <Typography variant="subtitle1" fontWeight="bold">
                            {unpostedSummary?.businessDate || new Date().toISOString().split('T')[0]}
                        </Typography>
                    </Paper>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: '#f8fafc' }}>
                        <Typography variant="caption" color="text.secondary" display="block">Unposted Orders</Typography>
                        <Typography variant="subtitle1" fontWeight="bold" sx={{ color: count > 0 ? 'error.main' : 'success.main' }}>
                            {count} {count === 1 ? 'Order' : 'Orders'}
                        </Typography>
                    </Paper>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: '#f8fafc' }}>
                        <Typography variant="caption" color="text.secondary" display="block">Total Unposted Amount</Typography>
                        <Typography variant="subtitle1" fontWeight="bold" sx={{ color: totalAmount > 0 ? 'error.main' : 'text.primary' }}>
                            {formatPrice(totalAmount)}
                        </Typography>
                    </Paper>
                </Box>

                <TableContainer component={Paper}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Order ID</TableCell>
                                <TableCell>Order Time</TableCell>
                                <TableCell>Destination / Room</TableCell>
                                <TableCell>Department</TableCell>
                                <TableCell align="right">Total Amount</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="center">Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loadingData ? (
                                <TableRow><TableCell colSpan={7} align="center"><CircularProgress size={24} /></TableCell></TableRow>
                            ) : unpostedCharges.length === 0 ? (
                                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 3, color: 'text.secondary' }}>No unposted charges found. All orders billed.</TableCell></TableRow>
                            ) : (
                                unpostedCharges.map((charge) => {
                                    const orderDisplayId = charge.orderId || charge.id;
                                    const amount = parseFloat(charge.totalAmount ?? charge.amount ?? 0);
                                    const roomLabel = charge.room?.roomNumber 
                                        ? `Room ${charge.room.roomNumber}` 
                                        : (charge.roomId && roomsMap[charge.roomId]) 
                                        ? `Room ${roomsMap[charge.roomId]}` 
                                        : charge.roomId 
                                        ? 'Room Service' 
                                        : charge.tableId 
                                        ? `Table ${charge.tableId}` 
                                        : 'Direct Sale';
                                    const deptLabel = charge.department || (charge.roomId ? 'Room Service' : charge.tableId ? 'Restaurant Dine-In' : 'Restaurant');
                                    const timeStr = charge.orderTime || charge.createdAt;
                                    const formattedTime = timeStr ? new Date(timeStr).toLocaleString('en-US', {
                                        month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                                    }) : 'N/A';

                                    return (
                                        <TableRow key={charge.id || charge.orderId} hover>
                                            <TableCell sx={{ fontWeight: 600 }}>
                                                {orderDisplayId}
                                            </TableCell>
                                            <TableCell sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                                                {formattedTime}
                                            </TableCell>
                                            <TableCell>
                                                <Chip 
                                                    label={roomLabel} 
                                                    size="small" 
                                                    variant="outlined" 
                                                    color={charge.roomId ? "primary" : "default"} 
                                                />
                                            </TableCell>
                                            <TableCell>{deptLabel}</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                                                {formatPrice(amount)}
                                            </TableCell>
                                            <TableCell>
                                                <Chip 
                                                    label={charge.status || 'Pending'} 
                                                    color={charge.status === 'Pending' ? "warning" : "error"} 
                                                    size="small" 
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <Button
                                                    size="small"
                                                    variant="text"
                                                    onClick={() => {
                                                        setSelectedOrder(charge);
                                                        setSelectedOrderId(charge.id || charge.orderId);
                                                        setOrderDetailsOpen(true);
                                                    }}
                                                >
                                                    View
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        );
    };

    const renderRoomDiscrepancies = () => (
        <Box>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                Verify Room Status
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Finds discrepancies between the physical room status (from housekeeping) and the expected system reservation status.
            </Typography>
            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Room Number</TableCell>
                            <TableCell>System Status</TableCell>
                            <TableCell>Physical Status</TableCell>
                            <TableCell>Action Required</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loadingData ? (
                            <TableRow><TableCell colSpan={4} align="center"><CircularProgress size={24} /></TableCell></TableRow>
                        ) : roomDiscrepancies.length === 0 ? (
                            <TableRow><TableCell colSpan={4} align="center">No room discrepancies found.</TableCell></TableRow>
                        ) : (
                            roomDiscrepancies.map((room) => (
                                <TableRow key={room.id || room.roomId || room.roomNumber}>
                                    <TableCell>{room.roomNumber}</TableCell>
                                    <TableCell><Chip label={room.systemState || room.systemStatus || 'Unknown'} color="primary" size="small" variant="outlined" /></TableCell>
                                    <TableCell><Chip label={room.physicalState || room.physicalStatus || 'Unknown'} color="secondary" size="small" variant="outlined" /></TableCell>
                                    <TableCell>{room.issue || room.actionRequired || 'Investigate manually'}</TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );

    const renderCityLedger = () => (
        <Box>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                City Ledger Validation
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Validates company and OTA ledgers and surfaces aggregated outstanding receivables for tracking.
            </Typography>
            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Company / OTA</TableCell>
                            <TableCell>Account Type</TableCell>
                            <TableCell align="right">Outstanding Balance</TableCell>
                            <TableCell>Status</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loadingData ? (
                            <TableRow><TableCell colSpan={4} align="center"><CircularProgress size={24} /></TableCell></TableRow>
                        ) : cityLedger.length === 0 ? (
                            <TableRow><TableCell colSpan={4} align="center">No outstanding receivables found in City Ledger.</TableCell></TableRow>
                        ) : (
                            cityLedger.map((ledger, idx) => (
                                <TableRow key={ledger.id || ledger.companyId || ledger.otaId || idx}>
                                    <TableCell>{ledger.name || ledger.companyName || ledger.otaName || 'Unknown'}</TableCell>
                                    <TableCell>{ledger.type || ledger.accountType || 'Corporate'}</TableCell>
                                    <TableCell align="right">{formatPrice(ledger.balance || ledger.outstandingBalance || ledger.amount || 0)}</TableCell>
                                    <TableCell>
                                        <Chip label={ledger.status || 'Active'} color="info" size="small" variant="outlined" />
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );

    const renderCashierClosures = () => (
        <Box>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                Cashier Closures & Payment Reconciliation
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Groups today's payments by payment method to verify variances and ensure all tills are closed.
            </Typography>

            <TableContainer component={Paper} sx={{ mb: 3 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Payment Method</TableCell>
                            <TableCell align="right">System Total</TableCell>
                            <TableCell align="right">Declared/Physical</TableCell>
                            <TableCell align="right">Variance</TableCell>
                            <TableCell>Status</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loadingData ? (
                            <TableRow><TableCell colSpan={5} align="center"><CircularProgress size={24} /></TableCell></TableRow>
                        ) : reconcilePayments.length === 0 ? (
                            <TableRow><TableCell colSpan={5} align="center">No payments recorded for today.</TableCell></TableRow>
                        ) : (
                            reconcilePayments.map((pay, idx) => {
                                const systemAmount = parseFloat(pay.systemAmount || pay.amount || pay.total || 0);
                                const physicalAmount = parseFloat(pay.physicalAmount || pay.declaredAmount || systemAmount);
                                const variance = parseFloat(pay.variance || Math.abs(systemAmount - physicalAmount) || 0);
                                const status = pay.status || (variance === 0 ? 'Reconciled' : 'Variance Detected');
                                return (
                                    <TableRow key={idx}>
                                        <TableCell><b>{pay.method || pay.paymentMethod || pay.type || 'Unknown'}</b></TableCell>
                                        <TableCell align="right">{formatPrice(systemAmount)}</TableCell>
                                        <TableCell align="right">{formatPrice(physicalAmount)}</TableCell>
                                        <TableCell align="right" sx={{ color: variance > 0 ? 'error.main' : 'inherit' }}>
                                            {formatPrice(variance)}
                                        </TableCell>
                                        <TableCell>
                                            <Chip label={status} color={variance > 0 ? 'error' : 'success'} size="small" variant="outlined" />
                                        </TableCell>
                                    </TableRow>
                                )
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {cashierShiftsClosed ? (
                <Alert severity="success" icon={<FactCheckIcon />}>
                    All Cashier Shifts have been successfully closed and reconciled for the day.
                </Alert>
            ) : (
                <Alert severity="warning">
                    Pending cashier shifts or payment variances detected. Instruct staff to reconcile their tills.
                </Alert>
            )}

            <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>Voided & Adjusted Transactions</Typography>
            <TableContainer component={Paper} sx={{ mb: 3 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Transaction ID</TableCell>
                            <TableCell>Original Amount</TableCell>
                            <TableCell>Void Reason</TableCell>
                            <TableCell>Actioned By</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loadingData ? (
                            <TableRow><TableCell colSpan={4} align="center"><CircularProgress size={24} /></TableCell></TableRow>
                        ) : voidedTransactions.length === 0 ? (
                            <TableRow><TableCell colSpan={4} align="center">No voided transactions found.</TableCell></TableRow>
                        ) : (
                            voidedTransactions.map((tx) => (
                                <TableRow key={tx.transactionId || tx.id}>
                                    <TableCell>{(tx.transactionId || tx.id || '').split('-')[0]}</TableCell>
                                    <TableCell>{formatPrice(tx.amount || 0)}</TableCell>
                                    <TableCell>{tx.voidReason || tx.description || 'Manager Override'}</TableCell>
                                    <TableCell>{tx.performedById ? tx.performedById.split('-')[0] : 'System'}</TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Box sx={{ p: 2, bgcolor: '#f8f9fa', borderRadius: 2, border: '1px solid #eee', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                    <Typography variant="subtitle2" color="error.main">Cashier Shift Status</Typography>
                    <Typography variant="body2" color="text.secondary">All cash drawers must be balanced and closed before audit.</Typography>
                </Box>
                <Button 
                    variant={cashierShiftsClosed ? "contained" : "outlined"} 
                    color={cashierShiftsClosed ? "success" : "primary"}
                    onClick={() => setCashierShiftsClosed(!cashierShiftsClosed)}
                >
                    {cashierShiftsClosed ? 'Shifts Reconciled & Closed' : 'Mark Shifts as Closed'}
                </Button>
            </Box>
        </Box>
    );

    const renderExceptionReport = () => {
        const hasExceptions = pendingArrivals.length > 0 || expectedDepartures.length > 0 || unpostedCharges.length > 0 || roomDiscrepancies.length > 0 || !cashierShiftsClosed;
        return (
            <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                        Exception Report
                    </Typography>
                    <Button 
                        variant="outlined" 
                        size="small"
                        startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />}
                        onClick={() => setExportModalOpen(true)}
                        disabled={isDownloadingException}
                        sx={standardSecondaryButtonSx}
                    >
                        {isDownloadingException ? 'Generating...' : 'Download Report'}
                    </Button>
                    <ExportFormatModal
                        open={exportModalOpen}
                        onClose={() => setExportModalOpen(false)}
                        onExport={handleDownloadExceptionReport}
                        isExporting={isDownloadingException}
                        title="Export Exception Report"
                    />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    The following report identifies all pending items that require attention before you can proceed with the Night Audit.
                </Typography>
                {loadingData ? <CircularProgress size={24} /> : (
                    hasExceptions ? (
                        <Alert severity="warning" sx={{ mb: 2 }} icon={<WarningAmberIcon />}>
                            There are unresolved exceptions. You must resolve critical blockers before proceeding.
                        </Alert>
                    ) : (
                        <Alert severity="success" sx={{ mb: 2 }}>
                            No exceptions found. The system is fully balanced and ready for night audit.
                        </Alert>
                    )
                )}
                
                <List sx={{ bgcolor: 'rgba(0,0,0,0.02)', borderRadius: 2 }}>
                    <ListItem>
                        <ListItemIcon><FactCheckIcon color={pendingArrivals.length === 0 ? "success" : "error"} /></ListItemIcon>
                        <ListItemText primary={`Pending Arrivals: ${pendingArrivals.length}`} secondary={pendingArrivals.length > 0 ? "Must be resolved to prevent no-show revenue loss." : "All arrivals processed."} />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon><FactCheckIcon color={expectedDepartures.length === 0 ? "success" : "warning"} /></ListItemIcon>
                        <ListItemText primary={`Expected Departures: ${expectedDepartures.length}`} secondary={expectedDepartures.length > 0 ? "Outstanding departures must be checked out." : "All departures processed."} />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon><FactCheckIcon color={unsettledFolios.length === 0 ? "success" : "warning"} /></ListItemIcon>
                        <ListItemText primary={`Unsettled Folios: ${unsettledFolios.length}`} secondary={unsettledFolios.length > 0 ? "Outstanding balances remain on active folios." : "All folios are settled."} />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon><FactCheckIcon color={unpostedCharges.length === 0 ? "success" : "error"} /></ListItemIcon>
                        <ListItemText 
                            primary={`Unposted Charges: ${unpostedSummary?.unpostedCount ?? unpostedCharges.length}${unpostedCharges.length > 0 ? ` (${formatPrice(unpostedSummary?.unpostedAmount ?? unpostedCharges.reduce((sum, item) => sum + parseFloat(item.totalAmount || item.amount || 0), 0))})` : ''}`} 
                            secondary={unpostedCharges.length > 0 ? "You must post all completed orders before running audit." : "All charges posted."} 
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon><FactCheckIcon color={roomDiscrepancies.length === 0 ? "success" : "error"} /></ListItemIcon>
                        <ListItemText primary={`Room Discrepancies: ${roomDiscrepancies.length}`} secondary={roomDiscrepancies.length > 0 ? "Resolve system vs physical mismatches." : "All rooms match."} />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon><FactCheckIcon color="info" /></ListItemIcon>
                        <ListItemText primary={`City Ledger Accounts: ${cityLedger.length}`} secondary="Outstanding receivables tracked." />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon><FactCheckIcon color={cashierShiftsClosed ? "success" : "error"} /></ListItemIcon>
                        <ListItemText primary={`Cashier Shifts: ${cashierShiftsClosed ? 'Closed' : 'Open'}`} secondary={cashierShiftsClosed ? "All cashier tills are reconciled." : "You must close all cashier shifts."} />
                    </ListItem>
                </List>
            </Box>
        );
    };

    const renderExecuteAudit = () => (
        <Box sx={{ mt: 2 }}>
            {isBypassMode ? (
                <Alert severity="warning" sx={{ mb: 3 }}>
                    <b>Bypass Mode Active:</b> Because this property is {hotelStatus}, the Night Audit will execute in Bypass Mode. It will successfully close the calendar business day but will automatically skip all automated room-rate posting checks.
                </Alert>
            ) : (
                <Alert severity="error" sx={{ mb: 3 }}>
                    Warning: Rolling the operational business day is an irreversible financial routine. This will officially close the current ledger day.
                </Alert>
            )}

            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                System Checklist Execution Log:
            </Typography>
            <List sx={{ bgcolor: 'rgba(0,0,0,0.02)', borderRadius: 2, mb: 4 }}>
                <ListItem>
                    <ListItemIcon><FactCheckIcon color="primary" /></ListItemIcon>
                    <ListItemText primary="Locks current ledger business date" />
                </ListItem>
                <ListItem>
                    <ListItemIcon><FactCheckIcon color={isBypassMode ? "disabled" : "primary"} /></ListItemIcon>
                    <ListItemText 
                        primary={isBypassMode ? "SKIPPED: Posts all pending automated recurring room rates" : "Posts all pending automated recurring room rates"} 
                        sx={{ textDecoration: isBypassMode ? 'line-through' : 'none', opacity: isBypassMode ? 0.6 : 1 }}
                    />
                </ListItem>
                <ListItem>
                    <ListItemIcon><FactCheckIcon color="primary" /></ListItemIcon>
                    <ListItemText primary="Generates system closing balances and daily logs" />
                </ListItem>
                <ListItem>
                    <ListItemIcon><FactCheckIcon color="primary" /></ListItemIcon>
                    <ListItemText primary="Rolls over the active system operational date to tomorrow" />
                </ListItem>
            </List>

            <FormControlLabel
                control={
                    <Checkbox 
                        checked={isConfirmed} 
                        onChange={(e) => setIsConfirmed(e.target.checked)} 
                        color="error"
                    />
                }
                label={<Typography fontWeight="bold">I confirm that I want to close the business day and execute the Night Audit.</Typography>}
            />
        </Box>
    );

    return (
        <>
            <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 2000, display: 'flex', flexDirection: 'column' }} open={isSubmitting}>
                <CircularProgress color="inherit" size={60} sx={{ mb: 3 }} />
                <Typography variant="h5" fontWeight="bold">Processing Night Audit...</Typography>
                <Typography variant="body1">Do not close or refresh this window.</Typography>
            </Backdrop>

            <Dialog open={open && !isSubmitting} onClose={!isSubmitting ? onClose : undefined} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3, minHeight: 500 } }}>
                <DialogTitle sx={{ borderBottom: '1px solid #eee', bgcolor: '#f4f6f8', color: '#0052cc', display: 'flex', alignItems: 'center' }}>
                    <LockClockIcon sx={{ mr: 1, fontSize: 28 }} /> Night Audit Checklist Dashboard
                </DialogTitle>
                <DialogContent sx={{ p: 4, display: 'flex', flexDirection: 'column' }}>
                    
                    <Stepper activeStep={activeStep} sx={{ mb: 4, px: 2 }} alternativeLabel>
                        {STEPS.map((label, index) => {
                            const isError = 
                                (index === 0 && pendingArrivals.length > 0) ||
                                (index === 1 && expectedDepartures.length > 0) ||
                                (index === 3 && unpostedCharges.length > 0) ||
                                (index === 4 && roomDiscrepancies.length > 0) ||
                                (index === 6 && !cashierShiftsClosed);

                            return (
                                <Step key={label} completed={activeStep > index}>
                                    <StepLabel 
                                        error={isError && activeStep !== index}
                                        StepIconProps={{ sx: { '&.Mui-active': { color: '#0052cc' }, '&.Mui-completed': { color: 'success.main' } } }}
                                    >
                                        <Typography variant="caption" fontWeight="medium" color={activeStep === index ? '#0052cc' : 'inherit'}>
                                            {label}
                                        </Typography>
                                    </StepLabel>
                                </Step>
                            );
                        })}
                    </Stepper>

                    <Box sx={{ flexGrow: 1, overflowY: 'auto', px: 1 }}>
                        {activeStep === 0 && renderPendingArrivals()}
                        {activeStep === 1 && renderExpectedDepartures()}
                        {activeStep === 2 && renderUnsettledFolios()}
                        {activeStep === 3 && renderUnpostedCharges()}
                        {activeStep === 4 && renderRoomDiscrepancies()}
                        {activeStep === 5 && renderCityLedger()}
                        {activeStep === 6 && renderCashierClosures()}
                        {activeStep === 7 && renderExceptionReport()}
                        {activeStep === 8 && renderExecuteAudit()}
                    </Box>

                </DialogContent>
                <DialogActions sx={{ p: 3, borderTop: '1px solid #eee', bgcolor: '#fbfbfb', justifyContent: 'space-between' }}>
                    <Button onClick={onClose} color="inherit" disabled={isSubmitting}>
                        Cancel
                    </Button>
                    <Box>
                        <Button 
                            disabled={activeStep === 0 || isSubmitting} 
                            onClick={handleBack} 
                            sx={{ mr: 1 }}
                        >
                            Back
                        </Button>
                        {activeStep === STEPS.length - 1 ? (
                            <Button 
                                onClick={handleSubmit} 
                                variant="contained" 
                                color="error"
                                disabled={!isConfirmed || isSubmitting}
                                startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
                            >
                                {isSubmitting ? 'Executing...' : 'Run Daily Closure'}
                            </Button>
                        ) : (
                            <Button variant="contained" color="primary" onClick={handleNext}>
                                Next Step
                            </Button>
                        )}
                    </Box>
                </DialogActions>
            </Dialog>

            <CancelReservationModal
                open={cancelModal.open}
                onClose={() => setCancelModal({ open: false, reservation: null })}
                reservation={cancelModal.reservation}
                tenantId={cancelModal.reservation?.tenantId || tenantId}
                currency={effectiveCurrency}
                onSuccess={() => {
                    fetchStepData();
                }}
                setNotification={(notif) => {
                    if (notif?.severity === 'error') {
                        toast.error(notif.message);
                    } else if (notif?.message) {
                        toast.success(notif.message);
                    }
                }}
            />

            <OrderDetailsDrawer
                open={orderDetailsOpen}
                onClose={() => {
                    setOrderDetailsOpen(false);
                    setSelectedOrderId(null);
                    setSelectedOrder(null);
                }}
                orderId={selectedOrderId}
                initialOrder={selectedOrder}
                onStatusUpdated={() => {
                    fetchStepData();
                }}
            />
        </>
    );
}
